"use client";

import type React from "react";
import { useState, useEffect } from "react";

import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { WhatsNew } from "@/common/model/WhatsNew";
import { Input, Label } from "@/components/ui";
import { formatDateForInput } from "@/helpers/date.helper";

type WhatsNewModalProps = {
  isOpen: boolean;
  whatsNew: WhatsNew | null;
  onClose: () => void;
  onSave: (whatsNew: Partial<WhatsNew>) => void;
};

export default function WhatsNewModal({
  isOpen,
  whatsNew,
  onClose,
  onSave,
}: WhatsNewModalProps) {
  const [formData, setFormData] = useState<Partial<WhatsNew>>({} as WhatsNew);

  useEffect(() => {
    setFormData({
      title: whatsNew?.title || "",
      url: whatsNew?.url || "",
      startDate: whatsNew?.startDate || new Date(),
      endDate: whatsNew?.endDate || new Date(),
    });
  }, [whatsNew]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name.includes("Date") ? new Date(value + "T00:00:00Z") : value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-backgroundcolor text-white">
        <DialogHeader>
          <DialogTitle>
            {whatsNew ? "Edit Notification" : "Add Notification"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              name="title"
              value={formData.title || ""}
              onChange={handleChange}
              className="bg-backgroundbox text-white"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="url">URL</Label>
            <Input
              id="url"
              name="url"
              value={formData.url || ""}
              onChange={handleChange}
              className="bg-backgroundbox text-white"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="startDate">Start Date</Label>
              <Input
                id="startDate"
                name="startDate"
                type="date"
                value={formatDateForInput(formData?.startDate)}
                onChange={handleChange}
                className="bg-backgroundbox text-white"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="endDate">End Date</Label>
              <Input
                id="endDate"
                name="endDate"
                type="date"
                value={formatDateForInput(formData?.endDate)}
                onChange={handleChange}
                className="bg-backgroundbox text-white"
                required
              />
            </div>
          </div>
        </form>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="text-white75 hover:text-white border-white75 hover:border-white"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            onClick={handleSubmit}
            className="bg-lightblue hover:bg-lightshadowblue2 text-white"
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
